import * as React from "react";
import { useRef, useState, useMemo, useEffect, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import {
  StyledCheckbox,
  StyledMenuItemContainer,
  StyledMenuListItemText,
  HtmlTooltipDisabled,
} from "../StyledComponents/styled";
import { erase_duplicates } from "../../../api_calls/utils";
import {
  estimateOptionsContentWidth,
  sortSelectorOptions,
} from "./selectorOptionUtils";
import SelectFieldShell from "./SelectFieldShell";
import useSelectDropdown from "./useSelectDropdown";
import { SELECT_COLOR, SELECT_HIGHLIGHT_COLOR } from "./selectConstants";

export default function MultiSelect({
  label,
  defaultVal,
  elemId,
  options,
  onChangeCall,
  fieldError,
  enableFullWidth,
  selectionCheck,
  usedFor,
  onBlurCall,
  onFocusCall,
  showSelectedValues,
  readOnlyFlag,
  disableSearch,
  additionalIconWithTooltip,
  additionalIconMarkup,
  onClearCall,
  allowDuplicates,
  sortOptions,
  disabledOptions,
  tooltipTitleForDisabledOptions,
}) {
  const optionRefs = useRef({});
  const [selectedValues, setSelectedValues] = useState(defaultVal);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [instanceId] = useState(
    () => elemId || `multi-select-${Math.random().toString(36).substr(2, 9)}`
  );

  const isDisabled = typeof readOnlyFlag !== "undefined" ? readOnlyFlag : false;
  const showSearch = typeof disableSearch !== "undefined" ? !disableSearch : true;
  const applySorting = typeof sortOptions !== "undefined" ? sortOptions : true;
  const isFilter = usedFor === "filter";

  const handleDropdownClosed = useCallback(() => {
    setHighlightedIndex(-1);
    onBlurCall?.();
  }, [onBlurCall]);

  const dropdown = useSelectDropdown({
    instanceId,
    showSearch,
    onClosed: handleDropdownClosed,
  });

  const {
    open,
    setOpen,
    searchTerm,
    isFocused,
    setIsFocused,
    close: closeDropdown,
  } = dropdown;

  const close = useCallback(
    (shouldRefocus = false) =>
      closeDropdown({ clearSearch: true, refocus: shouldRefocus }),
    [closeDropdown]
  );
  
  const shrink = isFocused || open || (selectedValues?.length ?? 0) > 0 || isFilter;

  useEffect(() => {
    setSelectedValues(defaultVal);
  }, [defaultVal]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  const isSelected = useCallback(
    (option) => selectedValues.some((val) => selectionCheck(option, val)),
    [selectedValues, selectionCheck]
  );

  // A row is unavailable when the option says so, or when the caller marks it unavailable.
  const isOptionDisabled = useCallback(
    (option) =>
      option?.disabled === true ||
      (typeof disabledOptions === "function" && disabledOptions(option)),
    [disabledOptions]
  );

  const handleSelect = useCallback((option) => {
    if (isDisabled || isOptionDisabled(option)) return;

    const alreadySelected = isSelected(option);
    const newSelection = alreadySelected
      ? selectedValues.filter((val) => !selectionCheck(option, val))
      : [...selectedValues, option];

    setSelectedValues(newSelection);
    onChangeCall(newSelection);
  }, [isDisabled, isOptionDisabled, isSelected, selectedValues, selectionCheck, onChangeCall]);

  // A field that becomes read-only while open must close; focus would otherwise keep the label raised.
  useEffect(() => {
    if (!isDisabled) return;
    setIsFocused(false);
    if (open) close(false);
  }, [isDisabled, open, close, setIsFocused]);

  const handleClear = (e) => {
    e.stopPropagation();
    if (isDisabled) return;

    setSelectedValues([]);
    onChangeCall([]);
    if (onClearCall) onClearCall();
    if (open) close();
    else onBlurCall?.();
  };

  const uniqueOptions = useMemo(() => typeof allowDuplicates !== "undefined" && allowDuplicates === true ? options : erase_duplicates(options), [options, allowDuplicates]);

  // Closed weekdays and hours stay in the list but are not part of Select All.
  const enabledOptions = useMemo(
    () => uniqueOptions.filter((opt) => !isOptionDisabled(opt)),
    [uniqueOptions, isOptionDisabled]
  );

  const handleSelectAll = useCallback(() => {
    if (isDisabled) return;

    const allSel = enabledOptions.length > 0 && enabledOptions.every((opt) => isSelected(opt));

    if (allSel) {
      setSelectedValues([]);
      onChangeCall([]);
    } else {
      setSelectedValues([...enabledOptions]);
      onChangeCall([...enabledOptions]);
    }
  }, [isDisabled, enabledOptions, isSelected, onChangeCall]);

  // MultiSelect and SingleSelect now share the same IP/numeric/text ordering.
  // Passing sortOptions={false} preserves caller order for the former NoSort use cases.
  const sortedOptions = useMemo(
    () => sortSelectorOptions(uniqueOptions, applySorting),
    [uniqueOptions, applySorting]
  );

  const filteredOptions = useMemo(() => {
    if (!showSearch) return sortedOptions;

    const searchValue = searchTerm.trim().toLowerCase();
    if (!searchValue) return sortedOptions;

    return sortedOptions.filter((option) => {
      const optionLabel = String(option?.label ?? "").toLowerCase();
      return optionLabel.includes(searchValue);
    });
  }, [sortedOptions, searchTerm, showSearch]);

  const handleSelectAllFiltered = useCallback(() => {
    if (isDisabled) return;

    const selectableFiltered = filteredOptions.filter((opt) => !isOptionDisabled(opt));
    const allFilteredSel =
      selectableFiltered.length > 0 && selectableFiltered.every((opt) => isSelected(opt));

    if (allFilteredSel) {
      const newSelection = selectedValues.filter(
        (val) => !selectableFiltered.some((opt) => selectionCheck(opt, val))
      );
      setSelectedValues(newSelection);
      onChangeCall(newSelection);
    } else {
      const optionsToAdd = selectableFiltered.filter((opt) => !isSelected(opt));
      const newSelection = [...selectedValues, ...optionsToAdd];
      setSelectedValues(newSelection);
      onChangeCall(newSelection);
    }
  }, [isDisabled, filteredOptions, isOptionDisabled, isSelected, selectedValues, selectionCheck, onChangeCall]);

  const isSearchActive = showSearch && searchTerm.trim().length > 0;

  // Global select-all is hidden in filter context; "Select All Results" is always
  // shown when a search is active so users can bulk-select filtered items in any context
  const showSelectAll = (usedFor !== "filter") || isSearchActive;

  const allSelected = useMemo(() => {
    if (enabledOptions.length === 0) return false;
    return enabledOptions.every((opt) => isSelected(opt));
  }, [enabledOptions, isSelected]);

  const someSelected = useMemo(() => {
    const selectedCount = enabledOptions.filter((opt) => isSelected(opt)).length;
    return selectedCount > 0 && selectedCount < enabledOptions.length;
  }, [enabledOptions, isSelected]);

  const allFilteredSelected = useMemo(() => {
    const selectableFiltered = filteredOptions.filter((opt) => !isOptionDisabled(opt));
    if (selectableFiltered.length === 0) return false;
    return selectableFiltered.every((opt) => isSelected(opt));
  }, [filteredOptions, isOptionDisabled, isSelected]);

  const someFilteredSelected = useMemo(() => {
    const selectableFiltered = filteredOptions.filter((opt) => !isOptionDisabled(opt));
    const selectedCount = selectableFiltered.filter((opt) => isSelected(opt)).length;
    return selectedCount > 0 && !allFilteredSelected;
  }, [filteredOptions, isOptionDisabled, isSelected, allFilteredSelected]);

  const getTotalOptionsCount = useCallback(() => {
    return filteredOptions.length + (showSelectAll ? 1 : 0);
  }, [filteredOptions.length, showSelectAll]);

  const scrollHighlightedIntoView = useCallback(
    (index) => {
      const key =
        index === 0 && showSelectAll
          ? "select-all"
          : `option-${index - (showSelectAll ? 1 : 0)}`;
      const element = optionRefs.current[key];
      if (element) element.scrollIntoView({ block: "nearest", behavior: "smooth" });
    },
    [showSelectAll]
  );

  const handleListKeyDown = useCallback(
    (e) => {
      if (!open) return;

      const totalCount = getTotalOptionsCount();
      if (totalCount === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev < totalCount - 1 ? prev + 1 : 0;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev > 0 ? prev - 1 : totalCount - 1;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "Enter":
        case " ":
          if (e.key === " " && e.target.tagName === "INPUT") return;
          e.preventDefault();
          e.stopPropagation();

          if (highlightedIndex < 0) return;

          if (highlightedIndex === 0 && showSelectAll) {
            if (isSearchActive) {
              handleSelectAllFiltered();
            } else {
              handleSelectAll();
            }
          } else {
            const optionIndex = showSelectAll ? highlightedIndex - 1 : highlightedIndex;
            if (filteredOptions[optionIndex] && !isOptionDisabled(filteredOptions[optionIndex])) {
              handleSelect(filteredOptions[optionIndex]);
            }
          }
          break;

        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          close();
          break;

        default:
          break;
      }
    },
    [
      open,
      getTotalOptionsCount,
      highlightedIndex,
      showSelectAll,
      isSearchActive,
      filteredOptions,
      close,
      scrollHighlightedIntoView,
      handleSelect,
      handleSelectAll,
      handleSelectAllFiltered,
      isOptionDisabled,
    ]
  );

  // Reported to the shell, which decides the final panel width.
  const dropdownContentWidth = useMemo(
    () => estimateOptionsContentWidth(sortedOptions.map((option) => option?.label)),
    [sortedOptions]
  );

  const renderDisplayContent = () => {
    // const numSelected = selectedValues.length;
    const numSelected = typeof allowDuplicates !== "undefined" && allowDuplicates === true
      ? uniqueOptions.filter((option) => isSelected(option)).length
      : selectedValues.length;
    const textColor = isDisabled ? "#62626E" : SELECT_COLOR;

    if (typeof showSelectedValues !== "undefined" && showSelectedValues === true) {
      if (numSelected === 0) {
        return isFilter ? (
          <Typography variant="body2" sx={{ color: textColor }}>
            All Results
          </Typography>
        ) : null;
      }
      return (
        <Typography variant="body2" sx={{ color: textColor }}>
          {selectedValues.map((val) => val.label).join(", ")}
        </Typography>
      );
    }

    if (numSelected === 0) {
      return isFilter ? (
        <Typography variant="body2" sx={{ color: textColor }}>
          All Results
        </Typography>
      ) : null;
    }

    if (allSelected) {
      return (
        <Typography variant="body2" sx={{ color: textColor }}>
          All
        </Typography>
      );
    }

    return (
      <Typography variant="body2" sx={{ color: textColor }}>
        {numSelected + " Selected"}
      </Typography>
    );
  };

  return (
    <SelectFieldShell
      dropdown={dropdown}
      elemId={elemId}
      label={label}
      shrink={shrink}
      disabled={isDisabled}
      enableFullWidth={enableFullWidth}
      canClear={selectedValues.length > 0}
      onClear={handleClear}
      fieldError={fieldError}
      showSearch={showSearch}
      compactSearch
      preserveListFocus
      valueContainerSx={{
        flexWrap: "nowrap",
        overflowX: "auto",
        overflowY: "hidden",
        gap: 0.5,
        whiteSpace: "nowrap",
        "&::-webkit-scrollbar": { height: 6 },
      }}
      onAnchorClick={() => {
        if (isDisabled) return;
        onFocusCall?.();
        if (open) {
          close(false);
          return;
        }
        setOpen(true);
      }}
      onAnchorFocus={() => {
        if (isDisabled) return;
        onFocusCall?.();
      }}
      onAnchorKeyDown={(event) => {
        if (isDisabled) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setOpen(true);
        }
        if (event.key === "Escape") close();
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          if (!open) setOpen(true);
        }
      }}
      onListKeyDown={handleListKeyDown}
      contentWidth={dropdownContentWidth}
      valueDisplay={renderDisplayContent()}
      endAdornment={
        additionalIconWithTooltip === true ? (
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center" }}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {additionalIconMarkup(setOpen)}
          </Box>
        ) : null
      }
    >
      {showSelectAll && (
        <StyledMenuItemContainer
          ref={(element) => {
            optionRefs.current["select-all"] = element;
          }}
          onClick={(event) => {
            event.stopPropagation();
            if (isSearchActive) handleSelectAllFiltered();
            else handleSelectAll();
          }}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseEnter={() => setHighlightedIndex(0)}
          sx={{
            display: "flex",
            alignItems: "center",
            marginRight: 0,
            backgroundColor:
              highlightedIndex === 0 ? SELECT_HIGHLIGHT_COLOR : "transparent",
          }}
        >
          <StyledCheckbox
            checked={isSearchActive ? allFilteredSelected : allSelected}
            indeterminate={isSearchActive ? someFilteredSelected : someSelected}
            onMouseDown={(event) => event.stopPropagation()}
          />
          <StyledMenuListItemText
            primary={isSearchActive ? "Select All Results" : "Select All"}
          />
        </StyledMenuItemContainer>
      )}

      {filteredOptions.map((option, index) => {
        const optionKey =
          allowDuplicates === true ? `${option?.label}-${index}` : option?.label ?? index;
        const listIndex = showSelectAll ? index + 1 : index;
        const optionDisabled = isOptionDisabled(option);

        const menuItem = (
          <StyledMenuItemContainer
            key={optionDisabled ? undefined : optionKey}
            ref={(element) => {
              optionRefs.current[`option-${index}`] = element;
            }}
            onClick={(event) => {
              event.stopPropagation();
              if (!optionDisabled) handleSelect(option);
            }}
            onMouseDown={(event) => event.stopPropagation()}
            onMouseEnter={() => setHighlightedIndex(listIndex)}
            sx={{
              display: "flex",
              alignItems: "center",
              marginRight: 0,
              opacity: optionDisabled ? 0.5 : 1,
              cursor: optionDisabled ? "default" : "pointer",
              backgroundColor:
                highlightedIndex === listIndex
                  ? SELECT_HIGHLIGHT_COLOR
                  : "transparent",
            }}
          >
            <StyledCheckbox
              checked={isSelected(option)}
              disabled={optionDisabled}
              onMouseDown={(event) => event.stopPropagation()}
            />
            <Typography
              variant="body2"
              className="selectorOptions"
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {option.label}
            </Typography>
          </StyledMenuItemContainer>
        );

        if (optionDisabled) {
          return (
            <HtmlTooltipDisabled
              key={optionKey}
              title={tooltipTitleForDisabledOptions ?? "This option is unavailable"}
              arrow
            >
              <span>{menuItem}</span>
            </HtmlTooltipDisabled>
          );
        }

        return menuItem;
      })}

      {filteredOptions.length === 0 && (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="body2" color="textSecondary">
            No options found
          </Typography>
        </Box>
      )}
    </SelectFieldShell>
  );
}
